import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ShopIdentityProvider, useShopIdentity } from '../contexts/ShopIdentityContext';
import { buttonClass } from './settings/SettingsUI';
import { LogOutIcon, SmartphoneIcon } from './icons/LucideIcons';

// The same store listings the sign-in screen links to (Login.js).
const APP_STORE_URL = 'https://apps.apple.com/app/bouqmark-driver/id6761919291';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.twigsfloral.driver';

/**
 * A driver signed in to the web app with the one-time password their shop
 * gave them. The server refuses every call until they choose their own
 * (403 PASSWORD_CHANGE_REQUIRED), and only the Driver app can change a
 * driver's password, so the app shows this instead of screens that would
 * all fail. Nothing below the gate is mounted.
 */
export function DriverPasswordChangeGate({ children }) {
  const { passwordChangeRequired } = useAuth();
  if (passwordChangeRequired) {
    return (
      <ShopIdentityProvider>
        <DriverPasswordChangeScreen />
      </ShopIdentityProvider>
    );
  }
  return <>{children}</>;
}

export default function DriverPasswordChangeScreen() {
  const { logout } = useAuth();
  const shop = useShopIdentity();
  const appName = shop && shop.name ? `the ${shop.name} driver app` : 'the driver app';

  return (
    <div className="min-h-screen bg-surface-alt py-12 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white border border-gray-300 rounded-lg p-6 text-center">
            <SmartphoneIcon size={24} className="mx-auto text-floral-700" />
            <h1 className="mt-3 text-xl font-semibold text-gray-900">Set your new password in the Driver app</h1>
            <p className="mt-2 text-sm text-gray-700" data-testid="driver-password-explainer">
              Your shop gave you a temporary password. Open {appName} on your phone, sign in with it, and choose a
              new password there. Then you can sign in here.
            </p>
            <div className="mt-5 flex flex-wrap justify-center items-center gap-2">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img src="/app-store-badge.svg" alt="Download on the App Store" style={{ height: '40px' }} />
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img src="/google-play-badge.png" alt="Get it on Google Play" style={{ height: '60px' }} />
              </a>
            </div>
            <div className="mt-5 pt-5 border-t border-gray-300">
              <button type="button" onClick={logout} className={buttonClass.secondary}>
                <LogOutIcon size={16} />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
